import React, { useMemo } from 'react'
import { toast } from 'react-toastify'
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'

// api
import { InboxType } from '../../../server/models/Inbox'

// utils
import { handleError } from '../../errors/handleError'
import { useAuth } from '../../contexts/useAuthUser'

// context
import { useMyInboxes } from '../../contexts/MyInboxesContext'

// components
import { Page, Section } from '../../components/Page'
import { Skeleton } from '../../components/Skeleton'
import { InboxSourcesCount } from './components/InboxSourcesCount'
import { InboxPublicationsList } from './components/InboxPublicationsList'
import { InboxMembersCount } from './components/InboxMembersCount'
import { SearchTermsAnalytics } from './components/SearchTermsAnalytics'
import { UsersTable } from './components/UsersTable'

import './styles.css'

export const SuperAdmin: React.FC = () => {
  const { authUser } = useAuth()
  const { inboxMemberships, loading } = useMyInboxes()

  // Extract inboxes from memberships and sort by recently created
  const inboxes = useMemo(() => {
    return inboxMemberships
      .filter(membership => membership.inbox)
      .map(membership => membership.inbox as InboxType)
      .sort((a, b) => {
        // Recently created inboxes at top
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
  }, [inboxMemberships])

  if (!authUser?.superAdmin) {
    return null
  }

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email)
      toast.success('Email address copied to clipboard 👍')
    } catch (error) {
      handleError(error, 'Failed to copy email address')
    }
  }

  return (
    <Page>
      <Section title="Super Admin">
        <Tabs>
          <TabList>
            <Tab>Folders</Tab>
            <Tab>Users</Tab>
            <Tab>Search Terms Analytics</Tab>
          </TabList>
          <TabPanel>
            <table className="super-inboxes-table">
              <thead>
                <tr>
                  <th>Folder</th>
                  <th>Email Address</th>
                  <th>Publications</th>
                  <th>Sources</th>
                  <th>Members</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {
                  loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={`skeleton-${index}`}>
                        <td>
                          <Skeleton width={200} />
                        </td>
                        <td>
                          <Skeleton width={200} />
                        </td>
                        <td>
                          <Skeleton width={150} />
                        </td>
                        <td>
                          <Skeleton width={100} />
                        </td>
                        <td>
                          <Skeleton width={100} />
                        </td>
                        <td>
                          <Skeleton width={150} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    inboxes.map((inbox) => (
                      <tr key={inbox._id}>
                        <td>
                          <span className="mobile-label">Folder:</span>
                          {inbox.name}
                        </td>
                        <td>
                          <span className="mobile-label">Email Address:</span>
                          {inbox.email ? (
                            <button
                              type="button"
                              className="super-inboxes-email-button"
                              onClick={() => handleCopyEmail(inbox.email)}
                              title="Click to copy"
                            >
                              {inbox.email}
                            </button>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>
                          <span className="mobile-label">Publications:</span>
                          <InboxPublicationsList inbox={inbox} />
                        </td>
                        <td>
                          <span className="mobile-label">Sources:</span>
                          <InboxSourcesCount inboxId={inbox._id} />
                        </td>
                        <td>
                          <span className="mobile-label">Members:</span>
                          {inbox.private ? 'Private Folder' : <InboxMembersCount inboxId={inbox._id} />}
                        </td>
                        <td>
                          <span className="mobile-label">Created At:</span>
                          {new Date(inbox.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )
                }
              </tbody>
            </table>
          </TabPanel>
          <TabPanel>
            <UsersTable />
          </TabPanel>
          <TabPanel>
            <SearchTermsAnalytics />
          </TabPanel>
        </Tabs>
      </Section>
    </Page>
  )
}
